import React, { useState, useRef, useEffect } from 'react';

interface ImageViewerProps {
  src: string;
  alt: string;
  isOpen: boolean;
  onClose: () => void;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ src, alt, isOpen, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset state when image changes or modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [isOpen, src]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          event.preventDefault();
          handleZoomIn();
          break;
        case '-':
          event.preventDefault();
          handleZoomOut();
          break;
        case 'r':
        case 'R':
          event.preventDefault();
          handleRotate();
          break;
        case '0':
          event.preventDefault();
          handleReset();
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleReset = () => {
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: event.clientX - position.x,
        y: event.clientY - position.y
      });
    }
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (event: React.WheelEvent) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.max(0.25, Math.min(5, prev + delta)));
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === containerRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const imageStyle = {
    transform: `
      translate(${position.x}px, ${position.y}px) 
      scale(${zoom}) 
      rotate(${rotation}deg)
    `,
    cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
  };

  return (
    <div 
      className={`tps-image-viewer ${isOpen ? 'show' : ''}`}
      ref={containerRef}
      onClick={handleBackdropClick}
    >
      <div className="tps-image-viewer-container">
        <button
          className="tps-image-viewer-close"
          onClick={onClose}
          type="button"
          aria-label="Close image viewer"
        >
          ×
        </button>

        <img
          ref={imageRef}
          src={src}
          alt={alt}
          className="tps-image-viewer-image"
          style={imageStyle}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          onError={(e) => {
            console.error('Image failed to load:', src);
          }}
          draggable={false}
        />

        <div className="tps-image-viewer-controls">
          <button
            className="tps-image-viewer-btn"
            onClick={handleZoomOut}
            disabled={zoom <= 0.25}
            type="button"
            title="Zoom out (-)"
          >
            <span>−</span>
          </button>

          <div className="tps-image-viewer-zoom-info">
            {Math.round(zoom * 100)}%
          </div>

          <button
            className="tps-image-viewer-btn"
            onClick={handleZoomIn}
            disabled={zoom >= 5}
            type="button"
            title="Zoom in (+)"
          >
            <span>+</span>
          </button>

          <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(255,255,255,0.3)' }}></div>

          <button
            className="tps-image-viewer-btn"
            onClick={handleRotate}
            type="button"
            title="Rotate (R)"
          >
            <span>⟲</span>
          </button>

          <button
            className="tps-image-viewer-btn"
            onClick={handleReset}
            type="button"
            title="Reset (0)"
          >
            <span>⌂</span>
          </button>

          <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(255,255,255,0.3)' }}></div>

          <button
            className="tps-image-viewer-btn"
            onClick={onClose}
            type="button"
            title="Close (Esc)"
          >
            <span>✕</span>
          </button>
        </div>

        {/* Help text */}
        
      </div>
    </div>
  );
};

export default ImageViewer;